import React from 'react';
import { SERIES } from './lib/colors';

/**
 * A per-row sparkline for the Detail tables (spec v2 §10).
 *
 * Hand-drawn SVG rather than a Recharts `<LineChart>`: there is one of these per row and
 * a table page holds 25, so 25 `ResponsiveContainer`s each with their own resize observer
 * is a measurable cost for a 60×18 graphic with no axes, no tooltip and no legend.
 *
 * ── The scaling is per row, and says so ─────────────────────────────────────────
 * Each sparkline is scaled to its OWN minimum and maximum. That makes the shape legible
 * on a row with 4 clicks next to a row with 4,000 — and it means the heights of two
 * sparklines are not comparable to each other. The caption on the column says so, because
 * a reader who assumes a shared axis will read a flat busy row as quieter than a spiky
 * quiet one.
 *
 * No number is derived from this: the geometry is a picture of `clicks` over time, and
 * every figure the reader can act on is in the numeric columns beside it (§8).
 *
 * Renders nothing for fewer than two points — a single dot is not a trend, and an empty
 * box in a table cell reads as data that failed to load.
 */
const WIDTH = 60;
const HEIGHT = 18;
const PAD = 1.5;

const Sparkline = ({ points, label }) => {
  const list = Array.isArray(points) ? points : [];
  if (list.length < 2) return null;

  const values = list.map((p) => Number(p?.clicks) || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const stepX = (WIDTH - PAD * 2) / (values.length - 1);

  const d = values
    .map((v, i) => {
      const x = PAD + i * stepX;
      // A flat series has no span to scale against; centring it is the honest picture —
      // dividing by zero would put every point at the top and read as a peak.
      const y = span === 0
        ? HEIGHT / 2
        : HEIGHT - PAD - ((v - min) / span) * (HEIGHT - PAD * 2);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg
      data-testid="sparkline"
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={label || 'Visits over the selected period'}
      className="inline-block align-middle"
    >
      <path d={d} fill="none" stroke={SERIES.clicks} strokeWidth="1.25" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

export default Sparkline;
